import { Badge, Button, Surface, Textarea } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ClaimPhotoCategory, ClaimStatus } from '@warehouse/shared';
import { ArrowLeft, Save, Trash2 } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { FullPageSpinner } from '../../../components/FullPageSpinner';
import { PageHeader } from '../../../components/PageHeader';
import { useConfirm } from '../../../components/ConfirmDialog';
import { formatDate, formatDateTime, formatQty } from '../../../lib/format';
import { errorMessage, toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import {
  changeClaimStatus,
  claimKeys,
  deleteClaim,
  deleteClaimPhoto,
  fetchClaim,
  uploadClaimPhoto,
} from '../api/claims-api';
import { ClaimStatusBadge } from '../components/ClaimStatusBadge';
import { ClaimPhotoGrid } from '../components/ClaimPhotoGrid';
import { useClaimStatuses } from '../hooks/useClaimStatuses';

/**
 * One claim, in either chrome. The server decides what this user may do
 * (`abilities`) — the buttons follow that, they do not compute it. The 6h/48h
 * windows and the admin lock therefore behave identically in the portal, in the
 * admin app and in a future scanner.
 */
export function ClaimDetailPage({ portal = false }: { portal?: boolean }): ReactNode {
  const { t } = useTranslation(['claims', 'common']);
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { statuses, label: statusLabel } = useClaimStatuses();
  const { confirm, dialog } = useConfirm();

  const query = useQuery({ queryKey: claimKeys.detail(id), queryFn: () => fetchClaim(id), enabled: id !== '' });
  useLiveQuery(['claims'], claimKeys.all);

  const claim = query.data;
  const [status, setStatus] = useState<ClaimStatus | ''>('');
  const [resolutionNote, setResolutionNote] = useState('');

  useEffect(() => {
    if (!claim) return;
    setStatus(claim.status);
    setResolutionNote(claim.resolutionNote ?? '');
  }, [claim?.id, claim?.status, claim?.resolutionNote]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: claimKeys.all });

  const statusMutation = useMutation({
    mutationFn: (next: { status: ClaimStatus; resolutionNote: string }) =>
      changeClaimStatus(id, { status: next.status, resolutionNote: next.resolutionNote }),
    onSuccess: async () => {
      await invalidate();
      toastSuccess(t('claims:detail.statusSaved'));
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const uploadMutation = useMutation({
    mutationFn: (upload: { file: File; category: ClaimPhotoCategory }) => uploadClaimPhoto(id, upload),
    onSuccess: async () => {
      await invalidate();
      toastSuccess(t('claims:photo.uploaded'));
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const removePhotoMutation = useMutation({
    mutationFn: (fileId: string) => deleteClaimPhoto(id, fileId),
    onSuccess: async () => {
      await invalidate();
      toastSuccess(t('claims:photo.removed'));
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteClaim(id),
    onSuccess: async () => {
      await invalidate();
      toastSuccess(t('claims:detail.deleted'));
      void navigate(portal ? '/portal/claims' : '/orders/claims');
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  if (query.isLoading) return <FullPageSpinner />;
  if (query.error) return <p className="text-danger">{errorMessage(query.error)}</p>;
  if (!claim) return null;

  const backTo = portal ? '/portal/claims' : '/orders/claims';
  const abilities = claim.abilities;
  const selectableStatuses = abilities.isManager
    ? statuses.filter((entry) => entry.active)
    : statuses.filter((entry) => entry.active && (entry.key === 'registered' || entry.key === 'resolved'));

  return (
    <div>
      <PageHeader
        breadcrumb={
          <Link to={backTo} className="inline-flex items-center gap-1 hover:text-ink">
            <ArrowLeft className="size-3" aria-hidden />
            {portal ? t('claims:portal.backToList') : t('claims:title')}
          </Link>
        }
        title={t('claims:detail.title', { claimNo: claim.claimNo })}
        subtitle={claim.restaurantName ?? undefined}
        actions={
          <>
            <ClaimStatusBadge status={claim.status} />
            {abilities.canDelete ? (
              <Button
                type="button"
                variant="danger"
                icon={<Trash2 />}
                loading={deleteMutation.isPending}
                onClick={() => {
                  void (async () => {
                    const ok = await confirm({
                      title: t('claims:detail.deleteTitle'),
                      message: t('claims:detail.deleteMessage', { claimNo: claim.claimNo }),
                      confirmLabel: t('common:actions.delete'),
                    });
                    if (ok) deleteMutation.mutate();
                  })();
                }}
              >
                {t('common:actions.delete')}
              </Button>
            ) : null}
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Surface variant="card" className="lg:col-span-2">
          <h2 className="mb-3 font-display text-lg text-ink">{t('claims:detail.sections.summary')}</h2>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Fact label={t('claims:detail.arrivalOrder')} value={claim.salesOrderNo ?? '—'} />
            <Fact label={t('claims:detail.deliveryDate')} value={formatDate(claim.deliveryDate)} />
            <Fact
              label={t('claims:list.columns.reason')}
              value={<Badge tone="neutral">{t(`claims:reason.${claim.reason}`)}</Badge>}
            />
            <Fact label={t('claims:detail.reportedBy')} value={claim.createdByName ?? claim.createdByEmail ?? '—'} />
            <Fact label={t('claims:detail.reportedAt')} value={formatDateTime(claim.createdAt)} />
            <Fact
              label={t('claims:list.columns.resolvedAt')}
              value={claim.resolvedAt ? formatDateTime(claim.resolvedAt) : '—'}
            />
          </dl>

          <h3 className="mt-5 text-sm font-medium text-ink-light">{t('claims:detail.description')}</h3>
          <p className="mt-1 whitespace-pre-line text-sm text-ink">
            {claim.description.trim() || t('claims:detail.noDescription')}
          </p>

          <h3 className="mt-6 font-display text-base text-ink">{t('claims:detail.sections.lines')}</h3>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-surface-border text-left text-xs uppercase text-ink-muted">
                  <th className="py-2 pr-3">#</th>
                  <th className="py-2 pr-3">{t('claims:form.article')}</th>
                  <th className="py-2 pr-3">{t('common:common.lot')}</th>
                  <th className="py-2 text-right">{t('claims:form.quantity')}</th>
                </tr>
              </thead>
              <tbody>
                {claim.lines.map((line) => (
                  <tr key={line.id} className="border-b border-surface-border/60">
                    <td className="py-2 pr-3 text-ink-muted">{line.lineNo}</td>
                    <td className="py-2 pr-3">
                      <span className="font-mono text-xs text-ink-muted">{line.articleNo ?? '—'}</span>{' '}
                      {line.articleName ?? '—'}
                    </td>
                    <td className="py-2 pr-3 text-ink-muted">{line.lotNo ?? '—'}</td>
                    <td className="py-2 text-right">
                      {formatQty(line.qtyBase, { unit: line.baseUnitCode ?? undefined })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="mt-6 font-display text-base text-ink">{t('claims:detail.sections.photos')}</h3>
          <div className="mt-2">
            <ClaimPhotoGrid
              claimId={claim.id}
              photos={claim.photos}
              canEdit={abilities.canAddPhotos}
              coldChain={claim.reason === 'cold_chain'}
              uploading={uploadMutation.isPending}
              onUpload={(file, category) => {
                uploadMutation.mutate({ file, category });
              }}
              onRemove={(fileId) => {
                removePhotoMutation.mutate(fileId);
              }}
            />
          </div>
        </Surface>

        <Surface variant="card">
          <h2 className="mb-3 font-display text-lg text-ink">{t('claims:detail.sections.handling')}</h2>

          {abilities.lockedByStatus && !abilities.isManager ? (
            <p className="mb-3 rounded-xl bg-warning/10 px-3 py-2 text-sm text-ink-light">
              {t('claims:detail.lockedByStatus')}
            </p>
          ) : null}

          <label className="block text-sm text-ink-light" htmlFor="claim-status">
            {t('claims:detail.changeStatus')}
          </label>
          <select
            id="claim-status"
            className="mt-1 w-full rounded-lg border border-surface-border bg-surface px-2 py-2 text-sm disabled:opacity-50"
            value={status}
            disabled={!abilities.canChangeStatus}
            onChange={(event) => {
              setStatus(event.target.value as ClaimStatus);
            }}
          >
            {selectableStatuses.map((entry) => (
              <option key={entry.key} value={entry.key}>
                {statusLabel(entry.key)}
              </option>
            ))}
          </select>

          <label className="mt-4 block text-sm text-ink-light" htmlFor="claim-resolution">
            {t('claims:detail.resolutionNote')}
          </label>
          <Textarea
            id="claim-resolution"
            className="mt-1 w-full"
            rows={4}
            value={resolutionNote}
            disabled={!abilities.canChangeStatus}
            placeholder={t('claims:detail.resolutionPlaceholder')}
            onChange={(event) => {
              setResolutionNote(event.target.value);
            }}
          />

          <Button
            type="button"
            className="mt-3 w-full"
            icon={<Save />}
            disabled={!abilities.canChangeStatus || status === ''}
            loading={statusMutation.isPending}
            onClick={() => {
              if (status !== '') statusMutation.mutate({ status, resolutionNote });
            }}
          >
            {t('common:actions.save')}
          </Button>

          <dl className="mt-5 space-y-2 text-xs text-ink-muted">
            {abilities.statusWindowEndsAt ? (
              <div>
                {t('claims:detail.statusWindowEndsAt', {
                  at: formatDateTime(abilities.statusWindowEndsAt),
                })}
              </div>
            ) : null}
            {abilities.editWindowEndsAt ? (
              <div>
                {t('claims:detail.editWindowEndsAt', { at: formatDateTime(abilities.editWindowEndsAt) })}
              </div>
            ) : null}
            {claim.resolvedByName ? (
              <div>
                {t('claims:detail.resolvedBy')}: {claim.resolvedByName}
              </div>
            ) : null}
          </dl>
        </Surface>
      </div>
      {dialog}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: ReactNode }): ReactNode {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-ink-muted">{label}</dt>
      <dd className="mt-0.5 text-ink">{value}</dd>
    </div>
  );
}
